import { PageNode, Connection, ConnectionSide } from '@/types/sitemap';

import { useState } from 'react';

interface Props {
  pages: PageNode[];
  connections: Connection[];
  selectedPageIds: string[];
  onRemoveConnection: (id: string) => void;
  onChangeStyle: (id: string, style: Connection['style']) => void;
  draggingConnection: { fromId: string; fromSide: ConnectionSide; toX: number; toY: number } | null;
  renderKey?: number;
}

const NODE_WIDTH = 280;
const CURVE_OFFSET = 80;

const getSideOffset = (side: ConnectionSide) => {
  switch (side) {
    case 'top': return { dx: 0, dy: -CURVE_OFFSET };
    case 'bottom': return { dx: 0, dy: CURVE_OFFSET };
    case 'left': return { dx: -CURVE_OFFSET, dy: 0 };
    case 'right': return { dx: CURVE_OFFSET, dy: 0 };
  }
};

export function ConnectionLines({ pages, connections, selectedPageIds, onRemoveConnection, onChangeStyle, draggingConnection }: Props) {
  const [hoveredConnId, setHoveredConnId] = useState<string | null>(null);
  const getNodeRect = (pageId: string) => {
    const el = document.querySelector(`[data-page-id="${pageId}"]`) as HTMLElement | null;
    const page = pages.find(p => p.id === pageId);
    if (!page) return { x: 0, y: 0, w: NODE_WIDTH, h: 80 };
    const w = el ? el.offsetWidth : NODE_WIDTH;
    const h = el ? el.offsetHeight : 80;
    return { x: page.x, y: page.y, w, h };
  };

  const getAnchorPoint = (pageId: string, side: ConnectionSide) => {
    const rect = getNodeRect(pageId);
    switch (side) {
      case 'top': return { x: rect.x + rect.w / 2, y: rect.y };
      case 'bottom': return { x: rect.x + rect.w / 2, y: rect.y + rect.h };
      case 'left': return { x: rect.x, y: rect.y + rect.h / 2 };
      case 'right': return { x: rect.x + rect.w, y: rect.y + rect.h / 2 };
    }
  };

  const getPath = (
    from: { x: number; y: number },
    to: { x: number; y: number },
    style: Connection['style'],
    fromSide?: ConnectionSide,
    toSide?: ConnectionSide
  ) => {
    if (style === 'straight') {
      return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
    }
    if (style === 'step') {
      const midX = (from.x + to.x) / 2;
      return `M ${from.x} ${from.y} L ${midX} ${from.y} L ${midX} ${to.y} L ${to.x} ${to.y}`;
    }
    const fromOff = fromSide ? getSideOffset(fromSide) : { dx: (to.x - from.x) * 0.4, dy: 0 };
    const toOff = toSide ? getSideOffset(toSide) : { dx: -(to.x - from.x) * 0.4, dy: 0 };
    const cx1 = from.x + fromOff.dx;
    const cy1 = from.y + fromOff.dy;
    const cx2 = to.x + toOff.dx;
    const cy2 = to.y + toOff.dy;
    return `M ${from.x} ${from.y} C ${cx1} ${cy1} ${cx2} ${cy2} ${to.x} ${to.y}`;
  };

  // Dynamically compute SVG bounds from all page positions
  const PADDING = 2000;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  for (const page of pages) {
    const rect = getNodeRect(page.id);
    if (rect.x < minX) minX = rect.x;
    if (rect.y < minY) minY = rect.y;
    if (rect.x + rect.w > maxX) maxX = rect.x + rect.w;
    if (rect.y + rect.h > maxY) maxY = rect.y + rect.h;
  }

  if (draggingConnection) {
    if (draggingConnection.toX < minX) minX = draggingConnection.toX;
    if (draggingConnection.toY < minY) minY = draggingConnection.toY;
    if (draggingConnection.toX > maxX) maxX = draggingConnection.toX;
    if (draggingConnection.toY > maxY) maxY = draggingConnection.toY;
  }

  if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 5000; maxY = 5000; }

  const svgLeft = minX - PADDING;
  const svgTop = minY - PADDING;
  const svgWidth = (maxX - minX) + PADDING * 2;
  const svgHeight = (maxY - minY) + PADDING * 2;

  return (
    <svg className="absolute pointer-events-none" style={{ zIndex: 0, left: svgLeft, top: svgTop, width: svgWidth, height: svgHeight, overflow: 'visible' }} viewBox={`${svgLeft} ${svgTop} ${svgWidth} ${svgHeight}`}>
      <defs>
        <marker id="arrowhead" markerWidth="6" markerHeight="5" refX="6" refY="2.5" orient="auto">
          <polygon points="0 0, 6 2.5, 0 5" fill="hsl(var(--foreground))" opacity="0.7" />
        </marker>
        <marker id="arrowhead-drag" markerWidth="6" markerHeight="5" refX="6" refY="2.5" orient="auto">
          <polygon points="0 0, 6 2.5, 0 5" fill="hsl(var(--foreground))" opacity="0.5" />
        </marker>
      </defs>

      {connections.map(conn => {
        const from = getAnchorPoint(conn.fromId, conn.fromSide);
        const to = getAnchorPoint(conn.toId, conn.toSide);
        const path = getPath(from, to, conn.style, conn.fromSide, conn.toSide);
        const midX = (from.x + to.x) / 2;
        const midY = (from.y + to.y) / 2;
        const isVisible = hoveredConnId === conn.id || selectedPageIds.includes(conn.fromId) || selectedPageIds.includes(conn.toId);

        return (
          <g
            key={conn.id}
            onMouseEnter={() => setHoveredConnId(conn.id)}
            onMouseLeave={() => setHoveredConnId(null)}
          >
            <path
              d={path}
              fill="none"
              stroke="hsl(var(--foreground))"
              strokeWidth="1.5"
              opacity="0.5"
              markerEnd="url(#arrowhead)"
            />
            {/* Thick invisible hit area for hover + clicking */}
            <path
              d={path}
              fill="none"
              stroke="transparent"
              strokeWidth="16"
              className="pointer-events-auto cursor-pointer"
              onClick={() => {
                const styles: Connection['style'][] = ['curved', 'straight', 'step'];
                const currentIdx = styles.indexOf(conn.style);
                onChangeStyle(conn.id, styles[(currentIdx + 1) % styles.length]);
              }}
            />
            {/* Delete button at midpoint */}
            {isVisible && (
              <g
                className="pointer-events-auto cursor-pointer"
                onClick={(e) => { e.stopPropagation(); onRemoveConnection(conn.id); }}
              >
                <circle
                  cx={midX}
                  cy={midY}
                  r="9"
                  fill="hsl(var(--card))"
                  stroke="hsl(var(--border))"
                  strokeWidth="1.5"
                  className="hover:stroke-destructive transition-colors"
                />
                <text
                  x={midX}
                  y={midY + 4}
                  textAnchor="middle"
                  fontSize="13"
                  fill="hsl(var(--destructive))"
                  fontWeight="bold"
                  className="pointer-events-none"
                >
                  ×
                </text>
              </g>
            )}
          </g>
        );
      })}

      {draggingConnection && (() => {
        const from = getAnchorPoint(draggingConnection.fromId, draggingConnection.fromSide);
        const to = { x: draggingConnection.toX, y: draggingConnection.toY };
        return (
          <path
            d={getPath(from, to, 'curved', draggingConnection.fromSide)}
            fill="none"
            stroke="hsl(var(--foreground))"
            strokeWidth="1.5"
            strokeDasharray="6 4"
            opacity="0.8"
            markerEnd="url(#arrowhead-drag)"
          />
        );
      })()}
    </svg>
  );
}
